/**
 * bankDetails — the seller's own bank, for the block a buyer pays against.
 *
 * ── Why settings and not columns ────────────────────────────────────────────
 * This is store identity, like the GSTIN, so a column on `stores` would be the
 * obvious home. It is stored as settings instead, and the reason is worth
 * recording: a column means a Prisma migration, a WatermelonDB schema bump and
 * migration on all three clients, a sync-registry entry and a re-locked schema
 * hash for the parity gate — a lot of moving parts to add four strings while a
 * pilot is running. RECEIPT_CONFIG already sets the precedent of invoice print
 * configuration living in settings, and settings already sync per store.
 *
 * ── Not to be confused with ─────────────────────────────────────────────────
 * `Supplier.bankDetails`, which is where the shop SENDS money. This is where
 * the shop RECEIVES it. The two are easy to mix up and expensive to mix up.
 */

import type { ReceiptBankDetails } from './contracts/ReceiptContracts';

export const BANK_SETTING_KEYS = {
  name: 'BANK_NAME',
  accountNo: 'BANK_ACCOUNT_NO',
  ifsc: 'BANK_IFSC',
  branch: 'BANK_BRANCH',
} as const;

/** Minimal shape of the settings store we read from (keeps this file UI-free). */
export interface BankSettingsReader {
  getSetting: (key: string) => string;
}

/**
 * Read the bank block out of settings.
 *
 * Returns undefined when nothing is filled in, so a template can ask
 * `data.bank ? ... : ''` and a shop that has not entered a bank simply does not
 * print the block — rather than printing a heading over three empty lines.
 */
export function loadBankDetails(settings: BankSettingsReader): ReceiptBankDetails | undefined {
  const name = settings.getSetting(BANK_SETTING_KEYS.name)?.trim() || '';
  const accountNo = settings.getSetting(BANK_SETTING_KEYS.accountNo)?.trim() || '';
  const ifsc = settings.getSetting(BANK_SETTING_KEYS.ifsc)?.trim().toUpperCase() || '';
  const branch = settings.getSetting(BANK_SETTING_KEYS.branch)?.trim() || '';

  if (!name && !accountNo && !ifsc && !branch) return undefined;
  return {
    name: name || undefined,
    accountNo: accountNo || undefined,
    ifsc: ifsc || undefined,
    branch: branch || undefined,
  };
}

/**
 * An IFSC is four letters, then 0, then six alphanumerics — RBI's format.
 * Used to warn, never to block: a shop with an unusual code still needs to
 * print its invoice, and refusing the save would just cost them the sale.
 */
export const IFSC_REGEX = /^[A-Z]{4}0[A-Z0-9]{6}$/;

export function isPlausibleIfsc(value: string): boolean {
  return IFSC_REGEX.test(value.trim().toUpperCase());
}
